/**
 * Per-column Trash bin modal.
 *
 * Shows deleted tasks grouped by:
 *   * Today (flat list)
 *   * Yesterday (flat list)
 *   * Older — collapsible section grouped by date
 *
 * "Today" and "Yesterday" are dynamic (local time). Restore returns the task
 * to the bottom of its original column on the live board.
 */
import { useEffect, useMemo, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ArrowUUpLeft, CaretDown, CaretRight } from "@phosphor-icons/react";

function localDayKey(iso) {
  if (!iso) return "unknown";
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function yesterdayKey() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function niceDate(key) {
  if (key === "unknown") return "Undated";
  const [y, m, d] = key.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

export default function AtlasTrashModal({ open, column, onClose, onRestore }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [olderOpen, setOlderOpen] = useState(false);
  const [openDates, setOpenDates] = useState({}); // {ymd: true}

  const load = async () => {
    if (!column) return;
    setLoading(true);
    try {
      const { data } = await api.get("/atlas/trash", { params: { column_key: column.key } });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, column?.key]);

  const buckets = useMemo(() => {
    const tKey = todayKey();
    const yKey = yesterdayKey();
    const today = [];
    const yesterday = [];
    const older = {}; // { ymd: [tasks...] }
    items.forEach((t) => {
      const k = localDayKey(t.deleted_at);
      if (k === tKey) today.push(t);
      else if (k === yKey) yesterday.push(t);
      else (older[k] ||= []).push(t);
    });
    const olderKeys = Object.keys(older).sort().reverse();
    return { today, yesterday, older, olderKeys };
  }, [items]);

  const restore = async (id) => {
    await onRestore(id);
    setItems((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="atlas-trash-modal">
        <DialogHeader>
          <DialogTitle>
            Trash · <span className="text-slate-500 font-normal">{column?.name}</span>
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="text-sm text-slate-500 py-6 text-center">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-slate-500 italic py-6 text-center">This bin is empty.</div>
        ) : (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <BucketSection title="Today" tasks={buckets.today} onRestore={restore} />
            <BucketSection title="Yesterday" tasks={buckets.yesterday} onRestore={restore} />
            {buckets.olderKeys.length > 0 && (
              <div className="rounded-md border border-slate-200">
                <button
                  type="button"
                  onClick={() => setOlderOpen(!olderOpen)}
                  className="w-full flex items-center justify-between px-3 py-2 text-xs font-bold uppercase tracking-widest text-slate-600 hover:bg-slate-50"
                  data-testid="atlas-trash-older-toggle"
                >
                  <span>Older ({buckets.olderKeys.length} day{buckets.olderKeys.length === 1 ? "" : "s"})</span>
                  {olderOpen ? <CaretDown size={12} /> : <CaretRight size={12} />}
                </button>
                {olderOpen && (
                  <div className="border-t border-slate-100 divide-y divide-slate-100">
                    {buckets.olderKeys.map((k) => (
                      <div key={k}>
                        <button
                          type="button"
                          onClick={() => setOpenDates((o) => ({ ...o, [k]: !o[k] }))}
                          className="w-full flex items-center justify-between px-3 py-1.5 text-[11px] text-slate-500 hover:bg-slate-50"
                          data-testid={`atlas-trash-date-${k}`}
                        >
                          <span>{niceDate(k)} · {buckets.older[k].length}</span>
                          {openDates[k] ? <CaretDown size={10} /> : <CaretRight size={10} />}
                        </button>
                        {openDates[k] && (
                          <div className="px-3 pb-2 space-y-1">
                            {buckets.older[k].map((t) => (
                              <TrashRow key={t.id} task={t} onRestore={restore} />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BucketSection({ title, tasks, onRestore }) {
  if (!tasks || tasks.length === 0) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">
        {title} <span className="text-slate-400">· {tasks.length}</span>
      </div>
      <div className="space-y-1">
        {tasks.map((t) => (
          <TrashRow key={t.id} task={t} onRestore={onRestore} />
        ))}
      </div>
    </div>
  );
}

function TrashRow({ task, onRestore }) {
  return (
    <div
      className="flex items-center justify-between gap-2 rounded-md bg-slate-50 border border-slate-200 px-2 py-1.5 text-[12px]"
      data-testid={`atlas-trash-row-${task.id}`}
    >
      <div className="min-w-0 flex-1 truncate text-slate-700">{task.text}</div>
      <button
        type="button"
        onClick={() => onRestore(task.id)}
        title="Restore"
        className="text-slate-400 hover:text-emerald-600 transition-colors"
        data-testid={`atlas-trash-restore-${task.id}`}
      >
        <ArrowUUpLeft size={14} weight="bold" />
      </button>
    </div>
  );
}
